import { Component, OnInit, Inject, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChartDataSets, ChartPoint, Chart } from 'chart.js';
import { Color, BaseChartDirective } from 'ng2-charts';
import { TrackCoreObject, CorrEntityID } from '../asp/TrackCoreObject';
import * as utils from '../common/utils';
import * as units from '../common/units';
import * as consts from '../common/consts';
import * as settings from '../common/settings';
import * as aspSettings from '../asp/aspSettings';
import * as aspObjects from '../asp/aspObjects';
import { ZIndexMngr } from '../common/ZIndexMngr';
import { Animations } from '@core_n/Animations';

var defaultLegendOnClick = Chart.defaults.global.legend.onClick;

//
// Chart point that holds actual altitude and system ID (for plots)
//
class SideViewChartPoint implements ChartPoint {
  x: number;
  y: string;
  altitude: number;
  systemID: number; // for plots

  constructor(time: number, altitude: number, systemID?: number) {
    this.x = time;
    this.altitude = altitude;
    this.systemID = systemID;
    this.calculateY();
  }

  calculateY() {
    this.y = units.altitudeValue(this.altitude, true);
  }
}
  
@Component({
  selector: 'app-side-view',
  templateUrl: './side-view.component.html',
  styleUrls: ['./side-view.component.scss'],
  animations: [
    Animations.WindowOpenCloseFadeHeight
  ]
})
export class SideViewComponent extends ZIndexMngr implements OnDestroy, OnInit, AfterViewInit {

  @ViewChild(BaseChartDirective, { static: true }) chart: BaseChartDirective; // member name must be "chart"

  chartOptions: any;
  chartColors: Color[] = [{}];
  chartData: ChartDataSets[] = [
    {
      // Track trail altitudes
      data: [], borderWidth: 1, pointRadius: 2, pointBorderWidth: 1, pointHitRadius: 3, lineTension: 0.2
    }
  ];

  trackTitle: string;
  frozen: boolean = false;

  private track: TrackCoreObject = null;
  private prevColor;
  
  private frozenTitle;
  private frozenColor;

  private displayUnits: settings.DisplayUnits;
  private chartUnits: string; // current units label

  private systemsMap: Map<number, number> = new Map(); // map between system ID and dataset index
  private plots: Map<number, Set<number>> = new Map(); // map between system ID and all its plots

  constructor(public dialogRef: MatDialogRef<SideViewComponent>,
              elementRef: ElementRef,
              @Inject(MAT_DIALOG_DATA) data: any) {
    super(elementRef);

    dialogRef.beforeClosed().subscribe(() => dialogRef.close(this));

    this.track = data.track;

    let style = getComputedStyle(document.body);
    let defaultColor = style.getPropertyValue('--default-color');
    let fieldLabelColor = style.getPropertyValue('--field-label-color');

    let _this = this;

    this.chartOptions = {
      maintainAspectRatio: false,
      responsive: true,
      responsiveAnimationDuration: 0,
      animation: { duration: 0 },
      legend: {
        labels: { fontColor: `${defaultColor}`, usePointStyle: true, boxWidth: 6 },
        onClick: function(event, legendItem) {
          if (legendItem.datasetIndex > 0) {
            defaultLegendOnClick.call(this, event, legendItem);
          }
        }
      },
      scales: {
        xAxes: [
          { type: 'linear', ticks: { fontColor: `${defaultColor}`, padding: 3, beginAtZero: false, min: 0 } }
        ],
        yAxes: [
          { gridLines: { color: `${fieldLabelColor}`, zeroLineColor: `${fieldLabelColor}`, drawTicks: true, tickMarkLength: 5 },
            ticks: { fontColor: `${defaultColor}`, padding: 3, beginAtZero: true }
          }
        ]
      },
      hover: {
        mode: 'nearest'
      },
      tooltips: {
        mode: 'nearest',
        callbacks: {
          label: function (tooltipItem, data) {
            return ` ${tooltipItem.value} ${_this.chartUnits}`;
          },
          title: function (tooltipItem, data) {
            let datasetIndex = tooltipItem[0].datasetIndex;
            let index = tooltipItem[0].index;
            let t = data.datasets[datasetIndex].data[index].x;
            let timeStr = `${t.toFixed(1)} sec`;
            if (datasetIndex == 0) {
              return timeStr;
            }
            else {
              return `[${data.datasets[datasetIndex].label}] ${timeStr}`;
            }
          }
        }
      }
    };    

    this.periodicUpdate();
    this.updateDisplayUnits();

    setInterval(function () { _this.periodicUpdate() }, consts.DIALOG_REFRESH_INTERVAL * 2);
    setInterval(function () { _this.updateDisplayUnits() }, consts.DIALOG_REFRESH_INTERVAL);
  }

  static createDialogConfig(track: TrackCoreObject) {
    let dialogConfig = utils.createDialogConfig();
    dialogConfig.panelClass = 'side-view-dialog';
    dialogConfig.maxWidth = `1200px`;
    dialogConfig.maxHeight = `800px`;
    dialogConfig.data = { track: track };
    return dialogConfig;
  }
  
  ngOnInit(): void {
    let dialogContainer: HTMLElement = this.elementRef.nativeElement.parentElement;
    dialogContainer.style.width = `800px`;
    dialogContainer.style.height = `550px`;
    this.dialogRaise();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.windowVisible.next(true);
    })
  }

  ngOnDestroy(): void {
    this.dialogClosed();
  }

  onLegendClick(event, legendItem) {
    // allow show/hide only for plots datasets
    if (legendItem.datasetIndex > 0) {
      defaultLegendOnClick.call(this, event, legendItem);
    }
  }

  private periodicUpdate() {
    if (!this.track)
      return;
    if (!this.track.active()) {
      this.track = null;
      return;
    }
   
    if (this.frozen) {
      // keep track title and color
      this.frozenTitle = this.track.getTitle();
      this.frozenColor = this.track.getColor();
    }
    else {
      // update track title and color
      this.trackTitle = this.track.getTitle();
      this.udpateColor(this.track.getColor());
    }

    // update data only if not frozen
    if (!this.frozen) {
      let dataLength = this.chartData[0].data.length;
      if (this.track.trail && this.track.trail.points.length > 0) {
        let firstTime = this.track.trail.times[0];

        if (this.track.trail.points.length > dataLength) {
          // Build current track trail data
          for (let i = dataLength; i < this.track.trail.points.length; i++) {
            let seconds = (this.track.trail.times[i] - firstTime) / 1000;
            (this.chartData[0].data as ChartPoint[]).push(new SideViewChartPoint(seconds, this.track.trail.points[i].z));
          }
        }
      
        // update plots datasets
        let hasPlots = this.track.corrPlots && this.track.corrPlots.size > 0;
        if (hasPlots)
          this.handlePlots(this.track.corrPlots, firstTime);
        let hasPrevPlots = this.track.prevCorrPlots && this.track.prevCorrPlots.size > 0;
        if (hasPrevPlots)
          this.handlePlots(this.track.prevCorrPlots, firstTime);
      }
    }
  }
  
  private systemName(systemID): string {
    let sensor = aspObjects.sensorsPool.getSensorBySensorID(systemID);
    let name = sensor.sic.toString();
    return name == '' ? systemID : name;
  }

  // create new dataset if needed
  private getDatasetIndex(systemID: number): number {
    if (this.systemsMap.has(systemID))
      return this.systemsMap.get(systemID);
    
    let systemName = this.systemName(systemID);

    let newData = {
      // Correlated plots altitudes
      // !!! i added the 2 in colorOfPlot temporary we need to figure out how to get the correct color
      data: [], label: `${systemName}`, type: 'scatter',
      pointBorderColor: this.getColor(aspSettings.colorOfPlot(2, 1), 1.0, 1.2),
      pointBackgroundColor: this.getColor(aspSettings.colorOfPlot(2, 1), 0.5, 1.2),
      borderColor: this.getColor(aspSettings.colorOfPlot(2, 1), 1.0, 1.2),
      backgroundColor: this.getColor(aspSettings.colorOfPlot(2, 1), 0.3, 1.2),
      borderWidth: 1, pointRadius: 1.5, pointBorderWidth: 1,
    };

    // find index such that plots datasets are sorted by system name
    let index = -1;
    for (let i = 1; i < this.chartData.length; i++) {
      if (this.chartData[i].label > systemName) {
        index = i;
        break;
      }
    }

    if (index == -1) {
      this.chartData.push(newData);
      index = this.chartData.length - 1;
    }
    else
      this.chartData.splice(index, 0, newData);

    this.systemsMap.set(systemID, index);
    
    return index;
  }

  private handlePlots(corrPlots: Set<CorrEntityID>, firstTime: number) {
    for (let corrPlot of corrPlots) {
      let systemID = corrPlot.systemID;
      let plotID = corrPlot.entityID;
      let datasetIndex = this.getDatasetIndex(systemID);
      let plot = aspObjects.plotsPool.getPlot(systemID, plotID);
      if (plot) {
        let newPlot = true;
        if (!this.plots.has(systemID)) {
          // create new plots set
          this.plots.set(systemID, new Set());
        }
        else {
          // check if plot in set
          newPlot = !this.plots.get(systemID).has(plotID);
        }
        if (newPlot) {
          this.plots.get(systemID).add(plotID);
          let seconds = (plot.timeStamp.getTime() - firstTime) / 1000;;
          (this.chartData[datasetIndex].data as ChartPoint[]).push(new SideViewChartPoint(seconds, plot.geometry.z, systemID));
        }
      }
    }
  }
  
  private updateDisplayUnits() {
    let displayUnitsChanged = this.displayUnits != settings.userSettings.displayUnits;
    if (displayUnitsChanged) {
      // Update units and recalculate 'y' values
      this.displayUnits = settings.userSettings.displayUnits;
      this.chartUnits = units.altitudeUnits();
      this.chartData[0].label = `Altitude (${this.chartUnits})`;
      for (let cd of this.chartData) {
        for (let d of cd.data)
          (d as SideViewChartPoint).calculateY();
      }
      if (this.chart)
        this.chart.update();
    }
  }

  // Convert track color to RGB string
  private getColor(trackColor: number[], opacity: number = 1, lighterFactor: number = 1): string {
    let r = trackColor[0] * 255 * lighterFactor;
    let g = trackColor[1] * 255 * lighterFactor;
    let b = trackColor[2] * 255 * lighterFactor;
    if (r > 255) r = 255;
    if (g > 255) g = 255;
    if (b > 255) b = 255;
    let color = `rgb(${r}, ${g}, ${b}, ${opacity})`;
    return color;
  }

  onFreeze() {
    this.frozen = !this.frozen;
    if (this.frozen) {
      this.frozenTitle = null;
      this.frozenColor = null;
    }
    else {
      if (this.frozenTitle)
        this.trackTitle = this.frozenTitle;
      if (this.frozenColor)
        this.udpateColor(this.frozenColor);
      this.periodicUpdate();
    }
  }

  private udpateColor(newColor) {
    if (!utils.isEqual(newColor, this.prevColor)) {
      let chartBorderColor = this.getColor(newColor, 1, 1.2);
      let chartBackColor = this.getColor(newColor, 0.3, 1.2);
      this.chartColors[0].borderColor = chartBorderColor;
      this.chartColors[0].backgroundColor = chartBackColor;
      this.prevColor = newColor;
    }
  }
}
